import {Component, inject} from '@angular/core';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {CommonService} from '../../../core/api/services/utility-services/common.service';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {NgClass} from '@angular/common';
import {UrlConstants} from '../../../core/constants/url.constants';
import {Router, RouterLink} from '@angular/router';
import {RegistrationDto} from '../../../core/models/registration/registrationdto';
import {RegistrationService} from '../../../core/api/services/registration/registration.service';
import {EnvironmentService} from '../../../../environments/services/environment.service';
import {SessionStorageConstants} from '../../../core/constants/session-storage.constant';
import {RegistrationConstants} from '../../../core/constants/registration.constants';
import {PasswordInputComponent} from '../../common/password-input/password-input.component';
import {RegistrationHelpComponent} from '../registration-help/registration-help.component';
import {StandardFormFieldComponent} from '../../common/standard-form-field/standard-form-field.component';
import {emailValidator, requiredNumberPattern, requiredTrimmed} from '../../../core/util/custom-form-validators-util';
import {EventTypeConstants} from '../../../core/constants/event-type.constants';

@Component({
    selector: 'app-registration',
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    NgClass,
    RouterLink,
    PasswordInputComponent,
    RegistrationHelpComponent,
    StandardFormFieldComponent,
  ],
    templateUrl: './registration.component.html',
    styleUrl: './registration.component.css'
})
export class RegistrationComponent {

    readonly translateService = inject(TranslateService);
    readonly registrationService = inject(RegistrationService);
    readonly commonService = inject(CommonService);
    readonly environmentService = inject(EnvironmentService);
    readonly router = inject(Router);
    readonly UrlConstants = UrlConstants;
    readonly RegistrationConstants = RegistrationConstants;

    page: number;
    supportUrl: string
    supportMail: string;
    registrationForm: FormGroup;
    registrationDtls: RegistrationDto = new RegistrationDto();
    errors: string[] = [];
    helpItems: string[] = [];
    email:string = "";

    showHelp: boolean = false;

    ngOnInit() {
        this.supportMail = this.getSupportMail();
        this.supportUrl = `mailto:${this.supportMail}?subject=Registration Help`;
        this.page = RegistrationConstants.REGISTRATION_PAGE_ONE
        this.inItForm();
      for (let i = 1; i <= 4; i++) {
        this.helpItems.push('registration.help.list.text.' + i);
      }
    }

    getSupportMail() {
        switch (this.environmentService.environment) {
            case 'localhost':
                return SessionStorageConstants.CRC_LOCAL_SUPPORT_MAIL
            case 'development':
                return SessionStorageConstants.CRC_DEV_SUPPORT_MAIL
            case 'staging':
                return SessionStorageConstants.CRC_STAGE_SUPPORT_MAIL
            case 'production':
                return SessionStorageConstants.CRC_PROD_SUPPORT_MAIL
            default:
                return SessionStorageConstants.CRC_LOCAL_SUPPORT_MAIL
        }
    }

    inItForm() {

        if (this.page === RegistrationConstants.REGISTRATION_PAGE_ONE) {
            this.registrationForm = new FormGroup({
                registrationNumber: new FormControl(this.registrationDtls.contactId??"", [requiredTrimmed(), requiredNumberPattern(),Validators.maxLength(9)]),
                registrationEmail: new FormControl(this.email??"", [Validators.required, emailValidator()]),
            });
        } else if (this.page === RegistrationConstants.REGISTRATION_PAGE_TWO) {
            this.registrationForm = new FormGroup({
                username: new FormControl("", [requiredTrimmed(), Validators.minLength(6), Validators.maxLength(16)]),
                password: new FormControl("", [Validators.required, Validators.minLength(8), Validators.maxLength(16)]),
                confirmPassword: new FormControl("", [Validators.required, Validators.minLength(8), Validators.maxLength(16)]),
            });
        }
    }

    submit() {
        this.errors = [];
        if (this.page === RegistrationConstants.REGISTRATION_PAGE_ONE) {
            this.submitRegistrationStep1();
        } else if (this.page === RegistrationConstants.REGISTRATION_PAGE_TWO) {
            this.submitRegistrationStep2();
        }
    }

    submitRegistrationStep2() {
        const userName = this.getTranslateMsg('userprofile.label.username');
        const password = this.getTranslateMsg('userprofile.label.password');
        const confrimPassword = this.getTranslateMsg('userprofile.label.confirmPassword');

        const userNameCtrl = this.registrationForm.controls['username'];
        const passwordCtrl = this.registrationForm.controls['password'];
        const confrimPasswordCtrl = this.registrationForm.controls['confirmPassword'];

        userNameCtrl.markAsDirty();
        passwordCtrl.markAsDirty();
        confrimPasswordCtrl.markAsDirty();

        this.registrationDtls.username = userNameCtrl.value.toString().trim();
        this.registrationDtls.password = passwordCtrl.value;
        this.registrationDtls.confirmPassword = confrimPasswordCtrl.value;

        if (this.registrationForm.invalid) {
            if (userNameCtrl.errors) {
                if (userNameCtrl.errors['required']) {
                    this.errors.push(this.getTranslateMsg("registration.validation.blank", {0: userName}))
                } else if (userNameCtrl.errors['maxlength'] || userNameCtrl.errors['minlength']) {
                    this.errors.push(this.getTranslateMsg("userprofile.validation.username.length", {0: userName}))
                }
            }
            if (passwordCtrl.errors) {
                if (passwordCtrl.errors['required']) {
                    this.errors.push(this.getTranslateMsg("registration.validation.blank", {0: password}))
                } else if (passwordCtrl.errors['maxlength'] || passwordCtrl.errors['minlength']) {
                    this.errors.push(this.getTranslateMsg("userprofile.validation.newPassword.length", {0: password}))
                }
            }
            if (confrimPasswordCtrl.errors) {
                if (confrimPasswordCtrl.errors['required']) {
                    this.errors.push(this.getTranslateMsg("userprofile.validation.newPassword.notEqualConfirmPassword", {0: confrimPassword}))
                }
            }
            if (userNameCtrl.errors || passwordCtrl.errors) {
                if (passwordCtrl.errors && !passwordCtrl.errors["required"])
                    this.errorValidation(userNameCtrl.value, passwordCtrl.value, confrimPasswordCtrl.value);
            }
        } else if (this.errorValidation(userNameCtrl.value, passwordCtrl.value, confrimPasswordCtrl.value)) {
            this.registrationService.registerUser(this.registrationDtls).subscribe((response) => {
                if (!response.success) {
                    response.errMsg.forEach(error => {
                        if (error && error !== "success") {
                            if (error.includes("username")) {
                                this.errors.push(this.getTranslateMsg(error, {0: userName}));
                            } else if (error.includes("password")) {
                                this.errors.push(this.getTranslateMsg(error, {0: password}));
                            } else if (error.includes("confirmPassword")) {
                                this.errors.push(this.getTranslateMsg(error, {0: confrimPassword}));
                            } else {
                                this.errors.push(this.getTranslateMsg(error, {0: password}));
                            }
                        }
                    })
                } else {
                  this.commonService.logEvent(EventTypeConstants.REGISTER, 'User successfully registered')
                    this.commonService.session().subscribe(() => {
                        this.router.navigateByUrl(response.redirectUrl);
                    });
                }
            });
        }
    }

    submitRegistrationStep1() {
        const regNamenControl = this.registrationForm.get('registrationNumber')!;
        const regEmailControl = this.registrationForm.get('registrationEmail');

        regEmailControl?.markAsDirty();
        regNamenControl?.markAsDirty();

        if (this.registrationForm.invalid) {
            if (regNamenControl?.invalid && regNamenControl.errors) {
                const registrationNumber = this.getTranslateMsg('registration.label.registrationNumber');
                if (regNamenControl.errors['required']) {
                    regNamenControl.setValue('');
                    this.errors.push(this.getTranslateMsg('registration.validation.blank', {0: registrationNumber}))
                } else if (regNamenControl.errors['maxlength']) {
                  regNamenControl.setValue('');
                  const params = {0: this.supportUrl, 1: this.supportMail}
                  this.errors.push(this.getTranslateMsg('registration.validation.notFound', params))
                }
                else {
                    this.errors.push(this.getTranslateMsg('registration.validation.registrationNumber.notNumeric', {0: registrationNumber}))
                }
            }
            if (regEmailControl?.invalid && regEmailControl?.errors) {
                const email = this.getTranslateMsg('registration.label.emailAddress');
                if (regEmailControl.errors['required']) {
                    this.errors.push(this.getTranslateMsg('registration.validation.blank', {0: email}))
                } else if (regEmailControl.errors['email']) {
                    this.errors.push(this.getTranslateMsg('userprofile.validation.invalidEmail', {0: email}))
                }
            }
        } else {

            this.registrationDtls.registrationNumber = regNamenControl?.value.toString().trim()
            regNamenControl.setValue(this.registrationDtls.registrationNumber);
            this.registrationDtls.emailAddress = regEmailControl?.value
            this.email=this.registrationDtls.emailAddress
            this.registrationDtls.contactId = regNamenControl?.value
            this.registrationService.validateRegistrationDtls(this.registrationDtls).subscribe(res => {
                if (res.responseMessage === "success") {
                    this.page = RegistrationConstants.REGISTRATION_PAGE_TWO
                    this.registrationDtls = res;
                    this.inItForm();
                } else {
                    let params = {};
                    if (res.responseMessage.includes("notFound")) {
                        params = {0: this.supportUrl, 1: this.supportMail}
                    }
                    this.errors.push(this.getTranslateMsg("registration." + res.responseMessage, params))
                }
            })
        }
    }

    errorValidation = (userName: string, password: string, confirmPassword: string): boolean => {
        const errorList: string[] = [];
        const userNameLabel = this.getTranslateMsg('userprofile.label.username');
        const passwordLabel = this.getTranslateMsg('userprofile.label.password');
        if (userName.includes(" ")) {
            errorList.push(this.getTranslateMsg("userprofile.validation.username.noSpaces", {0: userNameLabel}))
        }
        if (userName === this.registrationDtls.contactId.toString()) {
            errorList.push(this.getTranslateMsg("userprofile.validation.username.sameAsRegistrationNumber", {0: userNameLabel}))
        }
        if (password === userName) {
            errorList.push(this.getTranslateMsg("userprofile.validation.newPassword.equalsUsername", {0: passwordLabel}))
        }
        if (password !== confirmPassword && confirmPassword !== "") {
            errorList.push(this.getTranslateMsg("userprofile.validation.newPassword.notEqualConfirmPassword", {0: passwordLabel}))
        }
        if (password.includes(" ")) {
            errorList.push(this.getTranslateMsg("userprofile.validation.newPassword.noSpaces", {0: passwordLabel}))
        }
        this.errors.push(...errorList);
        return errorList.length === 0
    }

    getTranslateMsg = (key: string, params: {} = {}) => {
        return this.translateService.instant(key, params);
    }

  backtoRegistration() {
    this.page= RegistrationConstants.REGISTRATION_PAGE_ONE
    this.inItForm();
  }

  closeHelp() {
    this.showHelp = false;
  }

  clickShowHelp() {
    this.showHelp = true;
  }
}
